import { Box, IconButton, LinearProgress, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ErrorOutlineOutlined, KeyboardArrowDown, OpenInNew } from "@mui/icons-material";
import { ICellRendererParams } from "ag-grid-community";
import { AxiosInstance } from "axios";
import ArrowPopper from "components/app/ArrowPopper";
import AuthCheck from "components/app/AuthCheck";
import { classHOC } from "components/app/ClassWrapper";
import HtmlDialog from "components/dashboard/executive/components/HtmlDialog";
import styles from "components/portfolio/cellRenderer/ExpandPlanRenderer.module.scss";
import { FEATURE_KEY } from "constant";
import { random } from "lodash";
import moment from "moment";
import "moment-timezone";
import dynamic from "next/dynamic";
import Link from "next/link";
import { AuthProvider } from "pages";
import { FC, MouseEvent, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { LoadingStatus } from "types/app";
import { AuthDetail } from "types/auth";
import { ExpandPlanVersionDataType } from "types/portfolio";
import { ExpandPlanType, ExpandPlanTypeEnum } from "types/summary";
import saveAs from "file-saver";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    backgroundColor: "white",
    flexDirection: "column",
    borderRadius: 4,
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    margin: "16px 0px 0px 16px",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "16px 16px 0px 16px",
    "&:last-of-type": {
      marginBottom: 16,
    },
    flexWrap: "wrap",
    background: "#F8F9FB",
    borderRadius: 8,
    padding: "8px 12px",
  },
  rowLabel: {
    fontSize: 16,
    color: "#007AA3",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  buttonRow: {
    display: "flex",
  },
  pdfButton: {
    backgroundColor: "#F7644A",
    padding: 8,
    "&:hover": {
      backgroundColor: "#F7644A",
    },
  },
  excelButton: {
    backgroundColor: "#00AB50",
    padding: 8,
    marginLeft: 8,
    "&:hover": {
      backgroundColor: "#00AB50",
    },
  },
  pptButton: {
    backgroundColor: "#FC9D03",
    padding: 8,
    marginLeft: 8,
    "&:hover": {
      backgroundColor: "#FC9D03",
    },
  },
  icon: { color: "white" },
  rowValue: { fontSize: 14, color: "#3B3B3B" },
}));

interface ExpandPlanRendererState {
  anchorEl: HTMLElement | null;
  expandPlanVersionData: ExpandPlanVersionDataType[];
  loadingStatus: LoadingStatus;
  hrefURL: string;
}

interface ExpandPlanRendererProps {
  type: ExpandPlanType;
  authDetail: AuthDetail;
  axios: AxiosInstance;
}

interface PlanAlertsProps {
  fieldName: string;
  lastModifiedTime: number;
}

const renderAlerts = (alerts: PlanAlertsProps[]) => {
  const fieldMapping = {
    customerRisk: "Customer Health Assessment",
    customerRiskNote: "Customer Health Assessment Notes",
    meetingRisk: "Cloud Meetings and Messaging",
    meetingRiskNote: "Meetings and Messaging Workload Assessment Notes",
    callingRisk: "Cloud Calling",
    callingRiskNote: "Calling Workload Assessment Notes",
    contactCenterRisk: "Cloud Contact Center",
    contactCenterRiskNote: "Contact Center Workload Assessment Notes",
    deviceRisk: "Cloud Devices",
    deviceRiskNote: "Devices Workload Assessment Notes",
  };
  return (
    <div style={{ width: "600px" }}>
      <p className={styles.text}>Below field(s) has/have not been updated in the last 30 days</p>
      <br />
      <table
        style={{
          width: "600px",
          border: "1px solid #ddd",
          borderCollapse: "collapse",
        }}
      >
        <thead style={{ backgroundColor: "#f7f7f7" }}>
          <tr>
            <th style={{ border: "1px solid #ddd", padding: "8px 16px" }}>Field</th>
            <th style={{ border: "1px solid #ddd", padding: "8px 16px" }}>Last updated date</th>
          </tr>
        </thead>
        <tbody>
          {alerts.map((item, idx) => (
            <tr key={idx}>
              <td style={{ border: "1px solid #ddd", padding: "8px 16px" }}>
                {item.fieldName in fieldMapping
                  ? fieldMapping[item.fieldName as keyof typeof fieldMapping]
                  : ""}
              </td>
              <td style={{ border: "1px solid #ddd", padding: "8px 16px" }}>
                {item.lastModifiedTime
                  ? moment(item.lastModifiedTime)
                      .tz("America/Los_Angeles")
                      .format("MMM Do, YYYY(PT)")
                  : "N/A"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ExpandPlanRenderer: FC<ICellRendererParams & ExpandPlanRendererProps> = ({
  type,
  data,
  axios,
  node,
}) => {
  const classes = useStyles();
  // const authDetail = useContext(AuthProvider);
  const epCellRef = useRef<HTMLDivElement>(null);
  const epPopRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openSummarize, setOpenSummarize] = useState(false);
  const [summarize, setSummarize] = useState<string>("");
  const authDetail = useContext(AuthProvider);
  /* const [setExpandPlanVersionData] = useState<ExpandPlanVersionDataType[]>([]);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(
    "notLoaded"
  ); */
  const { expandPlanCreatedDate, partnerExpandPlanCreatedDate, epLastModifiedTime, planAlerts } =
    data;

  /* const monthBefore = moment()
    .tz("America/Los_Angeles")
    .subtract(30, "days")
    .valueOf();
  const filterPlanAlerts: PlanAlertsProps[] = (
    (planAlerts as PlanAlertsProps[]) ?? []
  ).filter(
    (item) =>
      item.lastModifiedTime !== null && item.lastModifiedTime < monthBefore
  ); */

  let createdDate: string;
  if (!epLastModifiedTime) {
    createdDate =
      type === ExpandPlanTypeEnum.Cisco ? expandPlanCreatedDate : partnerExpandPlanCreatedDate;
  } else {
    createdDate = type === ExpandPlanTypeEnum.Cisco ? epLastModifiedTime : "";
  }

  // const createdDate =
  //   this.props.type === ExpandPlanType.CISCO
  //     ? expandPlanCreatedDate
  //     : partnerExpandPlanCreatedDate;

  if (node.rowPinned) return <></>;

  if (type === ExpandPlanTypeEnum.Partner && !data?.lifecyclePartnerFlag) return <>N/A</>;

  useEffect(() => {
    const date = data?.summarizedDate
      ? moment(data?.summarizedDate).tz("America/Los_Angeles").format("MMM Do, YYYY (PT)")
      : "N/A";

    let txt = `<p style="font-size: 12px;">Summarized at: <span style="color: #007AA3; font-size: 10px;">${date}</span></p><br/>`;
    const lis = (data?.summarizedData ?? "").split("\n").filter((x: string) => x.trim() !== "");
    txt += lis.reduce((cur: string, prv: string) => {
      cur += `<li>${prv.replace("- ", "")}</li>`;
      return cur;
    }, "<ul>");
    setSummarize(txt + "</ul>");

    const closePop = (e: Event) => {
      if (
        !epPopRef.current?.contains(e.target as Node) &&
        epPopRef.current !== e.target &&
        !epCellRef.current?.contains(e.target as Node)
      ) {
        setAnchorEl(null);
      }
    };
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      const featureEnable = authDetail?.features.some((x) => x === FEATURE_KEY.EDIT_SUCCESS_PLAN);

      (featureEnable || (createdDate !== null && createdDate !== "")) &&
        setAnchorEl(event.currentTarget);
      /* const { accountKey } = data;
      (axios as AxiosInstance)
        .get(
          `/api/portfolio/expandPlanVersion/${accountKey}?managementType=${type}`
        )
        .then((res) => {
          const data = res.data;
          setExpandPlanVersionData(data);
          setLoadingStatus("loaded");
        })
        .catch((error) => {
          console.log(error);
          setLoadingStatus("failed");
        }); */
    }
  };

  const downloadHandler = (url: string) => () => {
    const toastContent = () => (
      <div>
        <p>Please wait while we are fetching the data</p>
        <LinearProgress
          color="primary"
          style={{ borderRadius: 5 }}
        />
      </div>
    );
    const toastId = `progress${random(5, true)}`;
    toast.info(toastContent(), {
      toastId,
      closeOnClick: false,
      autoClose: false,
      closeButton: true,
      hideProgressBar: false,
    });
    axios
      .get(url, {
        responseType: "blob",
        onDownloadProgress: (e) => {
          toast.update(toastId, {
            progress: e.loaded / e.total,
            render: (
              <div>
                <p>Please wait while we are fetching the data</p>
                <p>{((e.loaded * 100) / e.total).toFixed(0)}%</p>
              </div>
            ),
          });
        },
      })
      .then((res) => {
        saveAs(
          new Blob([res.data], {
            type: res.headers["content-type"],
          }),
          res.headers.filename
        );
      })
      .catch(() => {
        toast.error("Failed to download", {
          position: "top-center",
          autoClose: 3000,
        });
      })
      .finally(() => {
        toast.dismiss(toastId);
      });
  };

  const handleSummarizeOpen = () => {
    setOpenSummarize(true);
  };

  return (
    <div
      className="wrapper"
      style={{ cursor: "pointer" }}
      ref={epCellRef}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          onClick={handlePopoverOpen}
          style={{ display: "flex", alignItems: "center" }}
        >
          {createdDate ? (
            <>
              <span style={{ color: createdDate !== "" ? "green" : "red" }}>Yes &nbsp;</span>
              <span>({moment(createdDate).tz("America/Los_Angeles").format("MM/DD/YYYY")})</span>
            </>
          ) : (
            <span style={{ color: "red" }}>No</span>
          )}
          <KeyboardArrowDown style={{ fontSize: "1rem" }} />
        </div>

        {data?.summarizedData && (
          <AuthCheck id={FEATURE_KEY.VIEW_CISCO_EXPAND_PLAN}>
            <IconButton
              size="small"
              style={{ marginLeft: 10, height: "fit-content" }}
              onClick={handleSummarizeOpen}
            >
              <img
                src="/activity/bridgeit.svg"
                alt=""
              />
            </IconButton>
          </AuthCheck>
        )}
        {planAlerts && planAlerts.length > 0 && (
          <Tooltip
            title={renderAlerts(planAlerts)}
            placement="bottom"
            classes={{ tooltip: styles.tooltipPanel }}
          >
            <ErrorOutlineOutlined
              fontSize="small"
              style={{ color: "#00a0d1" }}
            />
          </Tooltip>
        )}
        <HtmlDialog
          open={openSummarize}
          onClose={() => setOpenSummarize(false)}
          title="Customer Health Assessment Notes Summarization"
          content={summarize}
        />
      </div>
      {anchorEl ? (
        <ArrowPopper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="left"
          disablePortal={false}
        >
          <div ref={epPopRef}>
            {createdDate ? (
              <div
                style={{ overflow: "auto" }}
                className={styles.discoveryRecordWrapper}
              >
                {
                  <>
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Link
                        // href={`/spng/summary/expand-plan?managementType=${type}&accountId=${data?.accountId}&version=current`}
                        href={
                          // authDetail?.toggles?.["success-plan-revamp"]
                          // ?
                          `/spng/summary/success-plan?accountId=${data?.accountId}`
                          // : `/spng/summary/expand-plan?managementType=${type}&accountId=${data?.accountId}&version=current`
                        }
                        passHref={true}
                        legacyBehavior
                      >
                        <a
                          className={styles.pointWrapper}
                          style={{ display: "flex", alignItems: "center" }}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <OpenInNew style={{ marginRight: 4, fontSize: "1rem" }} /> Show Details
                        </a>
                      </Link>
                      {type === ExpandPlanTypeEnum.Cisco ? (
                        <div className={classes.buttonRow}>
                          <IconButton
                            className={classes.pdfButton}
                            onClick={downloadHandler(
                              `/api/csdigital-blob/api/sp/v1/expand-plan/${data?.accountId}?downloadType=pdf&managementType=0`
                            )}
                            title="PDF version"
                            size="large"
                          >
                            <i className={`cui-icon icon icon-file-pdf_16 ${classes.icon}`}></i>
                          </IconButton>
                          <IconButton
                            className={classes.excelButton}
                            onClick={downloadHandler(
                              `/api/csdigital-blob/api/sp/v1/expand-plan/${data?.accountId}?downloadType=xlsx&managementType=0`
                            )}
                            title="Excel version"
                            size="large"
                          >
                            <i className={`cui-icon icon icon-file-excel_16 ${classes.icon}`}></i>
                          </IconButton>
                        </div>
                      ) : undefined}
                    </Box>
                  </>
                }
              </div>
            ) : (
              <AuthCheck id={FEATURE_KEY.EDIT_SUCCESS_PLAN}>
                <Link
                  // href={`/spng/summary/expand-plan?managementType=${type}&accountId=${data?.accountId}&version=current`}
                  href={
                    // authDetail?.toggles?.["success-plan-revamp"]
                    //   ?
                    `/spng/summary/success-plan?accountId=${data?.accountId}`
                    // : `/spng/summary/expand-plan?managementType=${type}&accountId=${data?.accountId}&version=current`
                  }
                  passHref={true}
                  legacyBehavior
                >
                  <a
                    className={styles.pointWrapper}
                    style={{ display: "flex", alignItems: "center" }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <OpenInNew style={{ marginRight: 4, fontSize: "1rem" }} /> Create Collab Success
                    Plan
                  </a>
                </Link>
              </AuthCheck>
            )}
          </div>
        </ArrowPopper>
      ) : null}
    </div>
  );
};
export default dynamic(() => Promise.resolve(classHOC(ExpandPlanRenderer)), {
  ssr: false,
});
